import type { Ref } from "vue";
import { computed, ref } from "vue";
import { defineStore } from 'pinia';
import { CardStatus, type Card, CardSystem } from "@/models/card";
import {
  CardApiService,
  type CardPostPayload,
  type CardPostResponse,
  type CardsGetPayload,
} from '@/services/cardApiService';
import { mapCard } from "@/utils/mappers";
import { useGtm } from "@gtm-support/vue-gtm";
import * as amplitude from "@amplitude/analytics-browser";
import mastercardLogo from "@/assets/images/card-systems/mastercard.svg";
import visaColoredLogo from "@/assets/images/card-systems/visa_colored.svg";
import amexLogo from "@/assets/images/card-systems/amex.svg";

export const useCardsStore = defineStore('cards', () => {

  const initialized = ref(false);
  const showCardExistsModal = ref<boolean>(false);
  const cards: Ref<Card[]> = ref([]);
  const activeCards = computed(() => cards.value.filter((card) => card.status === CardStatus.ACTIVE));

  const hasOnlyAmexCards = computed(() => {
    if (!initialized.value) return false;
    if (activeCards.value.length === 0) return false;
    return activeCards.value.every((card) => card.system === CardSystem.AMERICAN_EXPRESS);
  });

  function getCard(id: number): Card | undefined {
    return cards.value.find((card) => card.id === id);
  }

  async function fetchCards(payload: CardsGetPayload) {
    const response = await CardApiService.list(payload);
    cards.value = (response.data?.items ?? []).map((item) => mapCard(item));
    initialized.value = true;
  }

  async function createCard(payload: CardPostPayload): Promise<CardPostResponse> {
    const response = await CardApiService.create(payload);
    if (response.data?.card ?? false) {
      // Check if new card
      const cardIndex = cards.value.findIndex((card) => card.id === response.data!.card.id);
      if (cardIndex !== -1) {
        cards.value.splice(cardIndex, 1, mapCard(response.data!.card));
      } else {
        cards.value.push(mapCard(response.data!.card));
      }

      amplitude.track('Card Linked', {
        'Card Type': response.data!.card.system,
      });

      const gtm = useGtm();
      gtm?.trackEvent({
        event: 'cardLinked',
      });
    }
    return response;
  }

  async function setCardAsDefault(id: number) {
    await CardApiService.setAsDefault(id);
    cards.value.forEach((card) => {
      card.billing = card.id === id;
    });
  }

  async function restoreCard(id: number) {
    await CardApiService.resume(id);
    const card = cards.value.find((card) => card.id === id);
    if (card) {
      card.status = CardStatus.ACTIVE;
    }
  }

  async function pauseCard(id: number) {
    await CardApiService.pause(id);
    const card = cards.value.find((card) => card.id === id);
    if (card) {
      card.status = CardStatus.PAUSED;
    }
  }

  async function deleteCard(id: number) {
    await CardApiService.delete(id);
    const card = cards.value.find((card) => card.id === id);
    if (card) {
      card.status = CardStatus.DELETED;

      amplitude.track('Card Deleted', {
        'Card Type': card.system,
      });
    }
  }

  function getCardSystemLogo(system: CardSystem) {
    switch (system) {
      case CardSystem.MASTERCARD:
        return mastercardLogo;
      case CardSystem.VISA:
        return visaColoredLogo;
      case CardSystem.AMERICAN_EXPRESS:
        return amexLogo;
      default:
        return visaColoredLogo;
    }
  }

  function $reset() {
    initialized.value = false;
    cards.value = [];
  }

  return {
    showCardExistsModal,
    initialized,
    cards,
    activeCards,
    hasOnlyAmexCards,
    getCard,
    fetchCards,
    createCard,
    restoreCard,
    setCardAsDefault,
    pauseCard,
    deleteCard,
    getCardSystemLogo,
    $reset,
  };
});
